import type { RateInputRequirements, RateInputs, TypedFieldSpec } from 'shared';
import {
  emptyAddressForm,
  validateAddressForm,
  type AddressFieldErrors,
  type AddressFormState,
} from '@/lib/customer-form.js';

export interface RateInputsState {
  address: AddressFormState;
  fields: Record<string, string | number | boolean>;
  /** Keyed by the attestation line. Gates submit; never leaves the browser. */
  attested: Record<string, boolean>;
}

export interface RateInputErrors {
  address: AddressFieldErrors;
  fields: Partial<Record<string, string>>;
  attestations: Partial<Record<string, string>>;
}

export const NO_RATE_INPUT_REQUIREMENTS: RateInputRequirements = {
  buyer_address: false,
  fields: [],
  attestations: [],
};

export const emptyRateInputs: RateInputsState = {
  address: emptyAddressForm,
  fields: {},
  attested: {},
};

export const noRateInputErrors: RateInputErrors = { address: {}, fields: {}, attestations: {} };

export function validateRateInputs(
  requirements: RateInputRequirements,
  state: RateInputsState,
): RateInputErrors {
  const fields: RateInputErrors['fields'] = {};
  for (const spec of requirements.fields) {
    if (!spec.required) continue;
    const value = state.fields[spec.key];
    const answered = spec.type === 'boolean' ? typeof value === 'boolean' : hasText(value);
    if (!answered) fields[spec.key] = answerCopy(spec);
  }

  const attestations: RateInputErrors['attestations'] = {};
  for (const line of requirements.attestations) {
    if (state.attested[line] !== true) {
      attestations[line] = 'Confirm the statement above to continue.';
    }
  }

  return {
    // The same rules `customer-info` applies: a provider that prices on geography re-prices
    // at issuance, so an address good enough to rate on has to be good enough to contract on.
    address: requirements.buyer_address ? validateAddressForm(state.address) : {},
    fields,
    attestations,
  };
}

export function hasRateInputErrors(errors: RateInputErrors): boolean {
  return [errors.address, errors.fields, errors.attestations].some((group) =>
    Object.values(group).some((message) => message !== undefined),
  );
}

function hasText(value: string | number | boolean | undefined): boolean {
  return value !== undefined && String(value).trim().length > 0;
}

function answerCopy(spec: TypedFieldSpec): string {
  return spec.type === 'boolean' || spec.type === 'select'
    ? 'Choose an answer to continue.'
    : `Enter ${spec.label.toLowerCase()} to continue.`;
}

/** Undefined when the account declared nothing, so those dealers send the payload they always did. */
export function toRateInputsWire(
  requirements: RateInputRequirements,
  state: RateInputsState,
): RateInputs | undefined {
  if (!requirements.buyer_address && requirements.fields.length === 0) return undefined;

  return {
    ...(requirements.buyer_address
      ? {
          buyer_address: {
            street1: state.address.street1.trim(),
            city: state.address.city.trim(),
            state: state.address.state,
            postal_code: state.address.postalCode.trim(),
          },
        }
      : {}),
    fields: state.fields,
  };
}
